/* eslint-disable jsx-a11y/media-has-caption */
import { Box, useMediaQuery } from '@mui/material'
import { Button, Container, Typography } from '@evocolabs/orkaui'
import {
  IMAGEFOLDER,
  REGION,
  worldHearingDayTextAndMedia
} from '../../src/data/WorldHearingDayData'

import Call40 from '@evocolabs/icons/lib/Call40'
import ChevronDownDouble24 from '@evocolabs/icons/lib/ChevronDownDouble24'
import Footer2 from '@/src/components/Footer/Footer2'
import Header from '@/src/components/HeaderV2'
import Image from 'next/image'
import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { getEnvVar } from '@/env'
import { getImage } from '@/src/utils/media'
import styles from './styles.module.scss'
import theme from '@/src/theme'

type DeviceType = 'PC' | 'Mobile' | 'Tablet'
type DeviceBreakpointType = 'xs' | 'sm' | 'md' | 'dl'

const deviceBreakpoint: DeviceBreakpointType = 'md'

const HearingLossIsCommonTypography = {
  fontSize: '64px',
  fontWeight: 900,
  lineHeight: '80px',
  letterSpacing: '0.2px'
}

const getWorldHearingDayMediaPath = (fileName: string, isMobile: boolean) => {
  const devicePath: DeviceType = isMobile ? 'Mobile' : 'PC'
  return getImage(fileName, devicePath, IMAGEFOLDER, REGION)
}

const HeroSectionFullOfPossibility = ({ isMobile }) => {
  return (
    <Container>
      <Typography
        variant={isMobile ? 'h3' : 'h2'}
        sx={{
          marginBottom: '16px',
          textAlign: { xs: 'center', [deviceBreakpoint]: 'left' },
          color: theme.palette.primary[700]
        }}
      >
        {worldHearingDayTextAndMedia.heroSection.heading}
      </Typography>
      <Typography
        variant="h1"
        sx={{
          fontStyle: 'italic',
          color: theme.palette.primary[700],
          fontSize: '90px',
          fontWeight: 900,
          lineHeight: '118px',
          marginBottom: { xs: '40px', [deviceBreakpoint]: '64px' },
          textAlign: { xs: 'center', [deviceBreakpoint]: 'left' }
        }}
      >
        {worldHearingDayTextAndMedia.heroSection.title}{' '}
      </Typography>
      <video
        width="100%"
        autoPlay
        loop
        muted
        playsInline
        poster={getWorldHearingDayMediaPath(
          worldHearingDayTextAndMedia.heroSection.videoPosterName,
          isMobile
        )}
        key={
          isMobile
            ? 'world-hearing-day-hero-video-mobile'
            : 'world-hearing-day-hero-video-pc'
        }
      >
        <source
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.heroSection.videoName,
            isMobile
          )}
          type="video/mp4"
        />
      </video>
    </Container>
  )
}

const NegativeVoice = ({ isMobile }) => {
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', [deviceBreakpoint]: '80%' },
          aspectRatio: {
            xs: 'auto 618/535',
            [deviceBreakpoint]: 'auto 1070/378'
          },
          maxWidth: '880px'
        }}
      >
        <Image
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.negativeVoice.imageName,
            isMobile
          )}
          fill
          unoptimized
          alt="Negative Voice Image"
          key={
            isMobile ? 'negative-voice-image-mobile' : 'negative-voice-image-pc'
          }
        />
      </Box>
      <Typography
        variant="h2"
        color={theme.palette.primary[700]}
        sx={{ textAlign: 'center' }}
      >
        {worldHearingDayTextAndMedia.negativeVoice.heading}{' '}
      </Typography>
    </Container>
  )
}

const HearingLossIsCommon = ({ isMobile }) => {
  const backgroundImageURL = getWorldHearingDayMediaPath(
    worldHearingDayTextAndMedia.hearingLossIsCommon.backgroundImageName,
    isMobile
  )
  return (
    <Container
      sx={{
        backgroundImage: `url(${backgroundImageURL})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        height: '554px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'flex-end', [deviceBreakpoint]: 'center' }
      }}
      key={
        isMobile ? 'hearing-loss-is-common-mobile' : 'hearing-loss-is-common-pc'
      }
    >
      <Typography
        variant={isMobile ? 'h2' : 'h1'}
        sx={{
          ...HearingLossIsCommonTypography,
          color: '#ffffff',
          textAlign: 'center'
        }}
      >
        {worldHearingDayTextAndMedia.hearingLossIsCommon.heading}{' '}
      </Typography>
    </Container>
  )
}

const HearingLossIsNormal = ({ isMobile }) => {
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          textAlign: 'center'
        }}
      >
        <Typography variant="h2" color="#CC9A39" as="span">
          {worldHearingDayTextAndMedia.hearingLossIsNormal.heading[0]}{' '}
        </Typography>
        <Typography variant="h2" color={theme.palette.primary[700]} as="span">
          {worldHearingDayTextAndMedia.hearingLossIsNormal.heading[1]}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: 'auto 689 / 359',
          maxWidth: '480px'
        }}
      >
        <Image
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.hearingLossIsNormal
              .hearingLossGroupImageName,
            isMobile
          )}
          fill
          unoptimized
          key={
            isMobile
              ? 'hearing-loss-group-image-mobile'
              : 'hearing-loss-group-image-pc'
          }
          alt="Hearing Loss Is Normal Image"
        />
      </Box>
      <Box
        sx={{
          textAlign: 'center'
        }}
      >
        <Typography
          variant="body1"
          color={theme.palette.primary[700]}
          as="span"
        >
          {worldHearingDayTextAndMedia.hearingLossIsNormal.subtitle[0]}{' '}
        </Typography>
        <Typography
          variant="Body1"
          color={theme.palette.primary[700]}
          as="span"
        >
          {worldHearingDayTextAndMedia.hearingLossIsNormal.subtitle[1]}{' '}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.primary[700]}
          as="span"
        >
          {worldHearingDayTextAndMedia.hearingLossIsNormal.subtitle[2]}{' '}
        </Typography>
        <Typography
          variant="Body1"
          color={theme.palette.primary[700]}
          as="span"
        >
          {worldHearingDayTextAndMedia.hearingLossIsNormal.subtitle[3]}{' '}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.primary[700]}
          as="span"
        >
          {worldHearingDayTextAndMedia.hearingLossIsNormal.subtitle[4]}
        </Typography>
      </Box>
    </Container>
  )
}

const HearingTreatment = ({ isMobile }) => {
  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', [deviceBreakpoint]: 'row' },
          gap: { xs: '40px', [deviceBreakpoint]: '64px' },
          height: { xs: '100%', [deviceBreakpoint]: '660px' },
          overflow: { [deviceBreakpoint]: 'scroll' }
        }}
      >
        <Box
          sx={{
            flex: 1,
            gap: '24px',
            display: 'flex',
            flexDirection: 'column',
            textAlign: { xs: 'center', [deviceBreakpoint]: 'left' },
            position: { [deviceBreakpoint]: 'sticky' },
            paddingTop: '40px',
            top: '0px'
          }}
        >
          <Box>
            <Typography
              as="span"
              variant="h1"
              color={theme.palette.primary[700]}
            >
              {worldHearingDayTextAndMedia.hearingTreatment.heading[0]}{' '}
            </Typography>
            <Typography as="span" variant="h1" color="#CC9A39">
              {worldHearingDayTextAndMedia.hearingTreatment.heading[1]}{' '}
            </Typography>
            <Typography
              as="span"
              variant="h1"
              color={theme.palette.primary[700]}
            >
              {worldHearingDayTextAndMedia.hearingTreatment.heading[2]}{' '}
            </Typography>
            <Typography as="span" variant="h1" color="#918D8C">
              {worldHearingDayTextAndMedia.hearingTreatment.heading[3]}{' '}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color={theme.palette.primary[700]}
            sx={{ textAlign: { xs: 'center', [deviceBreakpoint]: 'left' } }}
          >
            {worldHearingDayTextAndMedia.hearingTreatment.subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: '100%'
          }}
        >
          <Box
            sx={{
              width: '100%',
              position: 'relative',
              aspectRatio: {
                xs: 'auto 386 / 873',
                [deviceBreakpoint]: 'auto 596 / 933'
              }
            }}
          >
            <Image
              src={getWorldHearingDayMediaPath(
                worldHearingDayTextAndMedia.hearingTreatment
                  .hearingTreatmentImageName,
                isMobile
              )}
              fill
              unoptimized
              key={
                isMobile
                  ? 'hearing-loss-treatment-flow-mobile'
                  : 'hearing-loss-treatment-flow-pc'
              }
              alt="Hearing Loss Treatment Flow Image"
            />
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

const HearingLossNotDefineYourself = ({ isMobile }) => {
  return (
    <Container
      sx={{
        backgroundColor: '#1E2E45',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '40px'
      }}
    >
      <Typography
        sx={{
          ...HearingLossIsCommonTypography,
          color: '#ffffff',
          textAlign: 'center'
        }}
        variant="h1"
      >
        {worldHearingDayTextAndMedia.hearingLossNotDefineYourself.heading}
      </Typography>
      <video
        width="100%"
        height="auto"
        autoPlay
        loop
        muted
        preload="auto"
        playsInline
        key={
          isMobile
            ? 'hearing-loss-not-define-yourself-mobile'
            : 'hearing-loss-not-define-yourself-pc'
        }
        poster={getWorldHearingDayMediaPath(
          worldHearingDayTextAndMedia.hearingLossNotDefineYourself
            .hearingLossNotDefineYourselfVideoPosterName,
          isMobile
        )}
      >
        <source
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.hearingLossNotDefineYourself
              .hearingLossNotDefineYourselfVideoName,
            isMobile
          )}
          type="video/mp4"
        />
      </video>
      <Typography
        variant="h3"
        color="#EBE2D3"
        sx={{
          textAlign: 'center'
        }}
      >
        {worldHearingDayTextAndMedia.hearingLossNotDefineYourself.subtitle}
      </Typography>
      <ChevronDownDouble24 forceStroke="#EBE2D3" size="72px" />
    </Container>
  )
}

const OrkaMissionBanner = ({ isMobile }) => {
  const backgroundImage = getWorldHearingDayMediaPath(
    worldHearingDayTextAndMedia.orkaMissionBanner.orkaMissionBannerImageName,
    isMobile
  )

  return (
    <Container
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      key={isMobile ? 'orka-mission-bg-mobile' : 'orka-mission-bg-pc'}
    >
      <Typography
        variant="h1"
        color="#ffffff"
        sx={{
          paddingTop: {
            xs: '128px',
            [deviceBreakpoint]: '212px',
            textAlign: 'center'
          },
          maxWidth: '936px'
        }}
      >
        {worldHearingDayTextAndMedia.orkaMissionBanner.heading}
      </Typography>
    </Container>
  )
}

const XinkeStory = ({ isMobile }) => {
  return (
    <Container
      sx={{
        backgroundColor: '#FAF7F3',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: { xs: 'column-reverse', [deviceBreakpoint]: 'row' },
        gap: { xs: '40px', md: '80px' }
      }}
    >
      <Box
        sx={{
          flex: '1',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: { xs: 'center', [deviceBreakpoint]: 'flex-start' }
        }}
      >
        <Typography variant="h3" color={theme.palette.primary[700]}>
          {worldHearingDayTextAndMedia.xinkeStory.heading}
        </Typography>
        <Typography variant="h4" color={theme.palette.primary[700]}>
          {worldHearingDayTextAndMedia.xinkeStory.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: '12px', mb: '40px' }}
          color={theme.palette.primary[700]}
        >
          {worldHearingDayTextAndMedia.xinkeStory.subtitle}
        </Typography>
        <Link href="/blogPage/taking-charge-of-your-hearing-loss">
          <Button
            variant="secondary"
            id="world-hearing-day-read-xinke-full-story"
          >
            {worldHearingDayTextAndMedia.xinkeStory.buttonText}
          </Button>
        </Link>
      </Box>
      <Box
        sx={{ flex: '1', aspectRatio: 'auto 572 / 641', position: 'relative' }}
      >
        <Image
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.xinkeStory.xinkeImagePath,
            isMobile
          )}
          fill
          unoptimized
          key={
            isMobile
              ? 'hearing-loss-group-image-mobile'
              : 'hearing-loss-group-image-pc'
          }
          alt="Hearing Loss Is Normal Image"
          style={{ borderRadius: '6px' }}
        />
      </Box>
    </Container>
  )
}

const OrkaMissionAndVision = ({ isMobile }) => {
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '40px', [deviceBreakpoint]: '96px' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', [deviceBreakpoint]: 'row' },
          justifyContent: { xs: 'center', [deviceBreakpoint]: 'space-between' }
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', [deviceBreakpoint]: '50%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
            '& > *': {
              textAlign: {
                xs: 'center !important',
                [deviceBreakpoint]: 'left !important'
              }
            }
          }}
        >
          <Typography variant="T1" color="#CC9A39">
            {worldHearingDayTextAndMedia.orkaMissionAndVision.missionHeading}
          </Typography>
          <Typography variant="h3" color={theme.palette.primary[700]}>
            {worldHearingDayTextAndMedia.orkaMissionAndVision.missionSubtitle}
          </Typography>
          <Typography variant="body1">
            {worldHearingDayTextAndMedia.orkaMissionAndVision.missionContent}
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', [deviceBreakpoint]: 'calc(50% - 80px)' },
            position: 'relative',
            aspectRatio: {
              xs: 'auto 413/462',
              [deviceBreakpoint]: 'auto 560/375'
            },
            marginTop: { xs: '24px', [deviceBreakpoint]: 0 }
          }}
        >
          <Image
            src={getWorldHearingDayMediaPath(
              worldHearingDayTextAndMedia.orkaMissionAndVision.missionImageName,
              isMobile
            )}
            fill
            unoptimized
            key={
              isMobile ? 'orka-mission-image-mobile' : 'orka-mission-image-pc'
            }
            alt="orka-mission-image"
            style={{ borderRadius: '6px' }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', [deviceBreakpoint]: 'row' },
          justifyContent: {
            xs: 'center',
            [deviceBreakpoint]: 'space-between'
          }
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', [deviceBreakpoint]: 'calc(50% - 80px)' },
            position: 'relative',
            aspectRatio: {
              xs: 'auto 413/462',
              [deviceBreakpoint]: 'auto 560/375'
            },
            order: { xs: 2, [deviceBreakpoint]: 0 },
            marginTop: { xs: '24px', [deviceBreakpoint]: 0 }
          }}
        >
          <Image
            src={getWorldHearingDayMediaPath(
              worldHearingDayTextAndMedia.orkaMissionAndVision.visionImageName,
              isMobile
            )}
            fill
            unoptimized
            key={isMobile ? 'orka-vision-image-mobile' : 'orka-vision-image-pc'}
            alt="orka-vision-image"
            style={{ borderRadius: '6px' }}
          />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', [deviceBreakpoint]: '50%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
            '& > *': {
              textAlign: {
                xs: 'center !important',
                [deviceBreakpoint]: 'left !important'
              }
            }
          }}
        >
          <Typography variant="T1" color="#CC9A39">
            {worldHearingDayTextAndMedia.orkaMissionAndVision.visionHeading}
          </Typography>
          <Typography variant="h3" color={theme.palette.primary[700]}>
            {worldHearingDayTextAndMedia.orkaMissionAndVision.visionSubtitle}
          </Typography>
          <Typography variant="body1">
            {worldHearingDayTextAndMedia.orkaMissionAndVision.visionContent}
          </Typography>
        </Box>
      </Box>
    </Container>
  )
}

const OrkaTwoHearingAids = ({ isMobile }) => {
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: '16px', [deviceBreakpoint]: '24px' },
        '& > *': {
          textAlign: 'center !important'
        }
      }}
    >
      <Typography variant="h4">
        {worldHearingDayTextAndMedia.orkaTwoHearingAids.heading}
      </Typography>
      <Typography
        variant="h2"
        sx={HearingLossIsCommonTypography}
        color={theme.palette.primary[700]}
      >
        {worldHearingDayTextAndMedia.orkaTwoHearingAids.title}
      </Typography>
      <video
        width="100%"
        height="auto"
        autoPlay
        loop
        muted
        preload="auto"
        playsInline
        key={isMobile ? 'orka-two-mobile' : 'orka-two-pc'}
        poster={getWorldHearingDayMediaPath(
          worldHearingDayTextAndMedia.orkaTwoHearingAids.orkaTwoVideoPosterName,
          isMobile
        )}
      >
        <source
          src={getWorldHearingDayMediaPath(
            worldHearingDayTextAndMedia.orkaTwoHearingAids.orkaTwoVideoName,
            isMobile
          )}
          type="video/mp4"
        />
      </video>
    </Container>
  )
}

const WorldHearingDay = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down(deviceBreakpoint))

  return (
    <>
      <NextSeo
        title="Orka | You are full of possibilities in this world hearing day"
        description="Orka's mission is to create a world with fewer limits and maximize human potential."
      />
      <Header isWhiteBg />
      <HeroSectionFullOfPossibility isMobile={isMobile} />
      <NegativeVoice isMobile={isMobile} />
      <HearingLossIsCommon isMobile={isMobile} />
      <HearingLossIsNormal isMobile={isMobile} />
      <HearingTreatment isMobile={isMobile} />
      <HearingLossNotDefineYourself isMobile={isMobile} />
      <OrkaMissionBanner isMobile={isMobile} />
      <XinkeStory isMobile={isMobile} />
      <OrkaMissionAndVision isMobile={isMobile} />
      <OrkaTwoHearingAids isMobile={isMobile} />
      <Footer2 />
    </>
  )
}

export default WorldHearingDay
